import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, MessageSquareQuote, Save, Pencil, X } from "lucide-react";
import AdminNav from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "od_admin_token";

export default function AdminReviews() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", text: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", text: "" });
  const [saving, setSaving] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/testimonials`);
      setItems(data);
    } catch {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  if (!token) return <Navigate to="/admin" replace />;

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      return toast.error("Name and review text are required");
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/testimonials`, form, auth);
      setItems((it) => [...it, data]);
      setForm({ name: "", role: "", text: "" });
      toast.success("Review added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, role: item.role || "", text: item.text });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: "", role: "", text: "" });
  };

  const saveEdit = async (id) => {
    if (!editForm.name.trim() || !editForm.text.trim()) {
      return toast.error("Name and review text are required");
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/testimonials/${id}`, editForm, auth);
      setItems((it) =>
        it.map((p) => (p.id === id ? { ...p, ...editForm } : p))
      );
      toast.success("Review updated");
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, auth);
      setItems((it) => it.filter((p) => p.id !== id));
      toast.success("Review deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#262626] focus:border-[#D6B98D] focus:ring-1 focus:ring-[#D6B98D]/40 rounded-sm px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <AdminNav onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Create form */}
        <section className="mb-12 p-8 lg:p-10 bg-[#111] border border-[#262626] rounded-sm">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D6B98D] mb-3">
            Add review
          </div>
          <h2 className="font-serif text-3xl text-white font-light mb-6 tracking-tight">
            New customer testimonial
          </h2>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                Name
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="new-review-name"
                className={inputCls}
                placeholder="e.g. Sarah M."
              />
            </div>
            <div className="md:col-span-4">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                Location / role
              </label>
              <input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="new-review-role"
                className={inputCls}
                placeholder="e.g. Portland, OR"
              />
            </div>
            <div className="md:col-span-4 flex items-end">
              <button
                type="submit"
                disabled={creating}
                data-testid="new-review-submit"
                className="w-full inline-flex items-center justify-center gap-2 py-3 bg-[#D6B98D] text-[#050505] hover:bg-[#E5CCA5] disabled:opacity-60 transition-all duration-300 text-xs uppercase tracking-[0.25em] font-bold rounded-sm"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add review
              </button>
            </div>
            <div className="md:col-span-12">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                Review text
              </label>
              <textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                data-testid="new-review-text"
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="Paste the customer's quote here…"
              />
            </div>
          </form>
        </section>

        {/* List */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
                Current reviews
              </div>
              <h2 className="font-serif text-3xl text-white font-light tracking-tight">
                {items.length} review{items.length === 1 ? "" : "s"}
              </h2>
            </div>
          </div>

          {loading ? (
            <div className="py-20 flex items-center justify-center text-white/50">
              <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div
              data-testid="empty-reviews"
              className="py-20 px-6 text-center border border-dashed border-[#262626] rounded-sm"
            >
              <MessageSquareQuote
                className="w-10 h-10 text-[#D6B98D] mx-auto mb-4"
                strokeWidth={1}
              />
              <p className="text-white/60 text-sm">
                No reviews yet. Your home page will show placeholder reviews until you add real ones above.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {items.map((r) => (
                <div
                  key={r.id}
                  data-testid={`review-item-${r.id}`}
                  className="relative p-6 bg-[#111] border border-[#262626] hover:border-[#D6B98D]/40 transition-colors rounded-sm"
                >
                  {editingId === r.id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className={inputCls}
                          data-testid={`edit-name-${r.id}`}
                          placeholder="Name"
                        />
                        <input
                          value={editForm.role}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          className={inputCls}
                          data-testid={`edit-role-${r.id}`}
                          placeholder="Location / role"
                        />
                      </div>
                      <textarea
                        value={editForm.text}
                        onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
                        rows={4}
                        className={`${inputCls} resize-none`}
                        data-testid={`edit-text-${r.id}`}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveEdit(r.id)}
                          disabled={saving}
                          data-testid={`save-edit-${r.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D6B98D] text-[#050505] hover:bg-[#E5CCA5] text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-all disabled:opacity-60"
                        >
                          <Save className="w-3.5 h-3.5" /> Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          data-testid={`cancel-edit-${r.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 border border-[#262626] hover:border-white/40 text-white/70 text-xs uppercase tracking-[0.25em] rounded-sm transition-all"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="font-serif italic text-lg text-white/85 leading-snug mb-5">
                        "{r.text}"
                      </p>
                      <div className="flex items-end justify-between gap-2">
                        <div>
                          <div className="text-sm text-white font-medium">{r.name}</div>
                          {r.role && (
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D6B98D] mt-1">
                              {r.role}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEdit(r)}
                            data-testid={`edit-review-${r.id}`}
                            className="p-2 text-white/40 hover:text-[#D6B98D] transition-colors"
                            title="Edit review"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(r.id)}
                            data-testid={`delete-review-${r.id}`}
                            className="p-2 text-red-400/70 hover:text-red-400 transition-colors"
                            title="Delete review"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
